import { Box, Button, IconButton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { API_GET, API_POST, DBRequest, useConfig } from '../../api/api';
import { useTranslation } from 'react-i18next';
import NoDataAnimation from '../animation/NoDataAnimation';
import { BillingPosition } from '../special/BillingPosition';
import FormFields from '../form/FormFields';
import * as Yup from 'yup';
import Loader from '../special/Loader';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import moment from 'moment';
import DateRangeIcon from '@mui/icons-material/DateRange';
import MoreTimeIcon from '@mui/icons-material/MoreTime';

const SelectTimeBilling = ({ onAddPositions, onClose = () => {} }) => {
  const config = useConfig();
  const { t } = useTranslation();

  const [apiData, setApiData] = useState(null);
  const [dataCollection, setDataCollection] = useState({
    endDate: moment().format('YYYY-MM-DD'),
    groupedBy: 'Day',
  });
  const [workPackageIds, setWorkPackageIds] = useState([]);
  const [isValid, setIsValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function handleChange(insert) {
    const newDataCollection = { ...dataCollection, ...insert };
    setDataCollection(newDataCollection);
    if (
      !newDataCollection.project ||
      !newDataCollection.startDate ||
      !newDataCollection.endDate
    ) {
      setApiData(null);
      setWorkPackageIds([]);
      return;
    }

    let params = [];
    if (newDataCollection.dateRangeStart)
      params.push(`startDate=${newDataCollection.dateRangeStart}`);
    if (newDataCollection.dateRangeEnd)
      params.push(`endDate=${newDataCollection.dateRangeEnd}`);

    DBRequest({
      config,
      path: `project-positions?projectId[eq]=${newDataCollection.project.id}&startDate=${newDataCollection.startDate}&endDate=${newDataCollection.endDate}`,
      method: API_GET,
      onResponse: setApiData,
      onLoading: setIsLoading,
    });
  }

  function handleSubmit(data) {
    const { startDate, endDate, groupedBy } = dataCollection;

    const reqData = {
      startDate,
      endDate,
      workPackageIds,
      groupedBy,
    };

    DBRequest({
      config,
      path: `work-packages/billable-time`,
      method: API_POST(reqData),
      isLoading: setIsLoading,
      onResponse: generatePositions,
    });
  }

  function generatePositions(data) {
    const positions = [];

    data.forEach((group) => {
      // create group
      const { startDate, endDate } = group;
      const format = config.general.dateOutputFormat;
      const formattedStartDate = moment(startDate).format(format);
      const formattedEndDate = moment(endDate).format(format);

      const isSameDay = formattedStartDate === formattedEndDate;
      const groupLabel = isSameDay
        ? `${dataCollection.project.label} - ${t(
            'billing for'
          )} ${formattedStartDate}`
        : `${t('billing for')} ${formattedStartDate} ${t(
            'until'
          )} ${formattedEndDate}`;

      positions.push({
        name: groupLabel,
        amount: 0,
        unit: '',
        price: 0,
        taxRate: 0,
        positionableId: null,
        positionableType: 'custom',
        taskTitle: '',
        pricePerHour: '',
        monthlyLimit: 0,
        status: 0,
        hoaiId: null,
        isGroup: true,
        dateRangeStart: startDate,
        dateRangeEnd: endDate,
        depth: 0,
        type: 'default',
      });
      // create positions
      group.positions.forEach((pos) => {
        const {
          amount,
          description,
          name,
          price,
          taxRate,
          timeTrackingIds,
          unit,
          type,
          image,
          timeTrackingComments,
          users,
        } = pos;
        // positions
        var unitToUse = type === 'time' ? 'h' : unit;

        const userDescription = generateDescription(
          timeTrackingComments,
          users
        );

        positions.push({
          timeTrackingIds,
          name,
          amount,
          unit: unitToUse,
          price,
          taxRate,
          positionableId: null,
          positionableType: 'custom',
          taskTitle: '',
          pricePerHour: '',
          monthlyLimit: 0,
          status: 0,
          hoaiId: null,
          isGroup: false,
          dateRangeStart: startDate,
          dateRangeEnd: endDate,
          description: (description || ' ') + userDescription,
          depth: 1,
          image,
          type: 'default',
        });
      });
    });

    onAddPositions(positions);
    onClose();
  }

  function generateDescription(timeTrackingComments, users) {
    var userComment = '';

    // map user to id
    var userMap = {};
    users.forEach((user) => {
      userMap[user.id] = user;
    });

    // generate comments
    timeTrackingComments.forEach((element) => {
      const { comment, userId } = element;
      if (!comment) return;
      const user = userMap[userId];
      userComment += `<p><strong class="ql-size-12px" style="font-size:12px">${user.salutation} ${user.lastName}</strong></p>${comment}`;
    });

    return userComment;
  }

  const fields = [
    {
      label: t('position structure'),
      key: 'groupedBy',
      type: 'smallIconCards',
      size: 'wide',
      colspan: 2,
      cards: [
        {
          Icon: DateRangeIcon,
          label: t('Any Time'),
          value: 'Time',
        },
        {
          Icon: MoreTimeIcon,
          label: t('Daily Basis'),
          value: 'Day',
        },
        {
          Icon: DateRangeIcon,
          label: t('Position Wise'),
          value: 'Position',
        },
      ],
    },
    {
      label: t('Project'),
      key: 'project',
      openOnStart: true,
      type: 'projectSearch',
      colspan: 2,
    },
    {
      label: t('Start Date'),
      key: 'startDate',
      type: 'date',
    },
    {
      label: t('End Date'),
      key: 'endDate',
      type: 'date',
    },
  ];

  const validationSchema = Yup.object().shape({
    project: Yup.object().shape({
      id: Yup.string().required(t('Is required')),
    }),
    startDate: Yup.string().required(t('Is required')),
    endDate: Yup.string().required(t('Is required')),
  });

  const [allWorkPackageIds, setAllWorkPackageIds] = useState([]);
  function countWorkPackages() {
    const wpIds = [];
    apiData?.data.forEach((position) => {
      const { workPackages } = position;
      workPackages.forEach((wp) => {
        wpIds.push(wp.id);
      });
    });
    return wpIds;
  }
  useEffect(() => {
    if (apiData) setAllWorkPackageIds(countWorkPackages());
  }, [apiData]);

  const SelectedIcon =
    workPackageIds?.length === allWorkPackageIds?.length
      ? CheckBoxIcon //selected
      : workPackageIds?.length === 0
      ? CheckBoxOutlineBlankIcon //nothing selected
      : IndeterminateCheckBoxIcon;

  function handleMainSelect(e) {
    // remove all items from selection
    if (workPackageIds.length === allWorkPackageIds.length) {
      return setWorkPackageIds([]);
    }

    // add all items to selection
    return setWorkPackageIds(allWorkPackageIds);
  }

  return (
    <Box className="flex flex-col gap-2 h-full justify-between">
      <Loader active={isLoading} />
      <Box className="flex flex-col gap-4 overflow-y-auto">
        <Box className="flex justify-center my-2">
          <Typography fontSize={22}>{t('Time Billing')}</Typography>
        </Box>
        <Box className="grid grid-cols-2 gap-4">
          <FormFields
            fields={fields}
            updateDataCollection={handleChange}
            onValidationChange={setIsValid}
            validationSchema={validationSchema}
            dataCollection={dataCollection}
          />
        </Box>
        <Box className="flex flex-col gap-2">
          {apiData && apiData.data.length > 0 && (
            <Box className="flex">
              <Box
                className="flex items-center gap-2 pl-2 cursor-pointer"
                onClick={handleMainSelect}
              >
                <IconButton onClick={handleMainSelect}>
                  {SelectedIcon && <SelectedIcon />}
                </IconButton>
                <Typography
                  fontWeight={500}
                  fontSize={12}
                  className="opacity-50"
                >
                  {t('All Elements')}
                </Typography>
              </Box>
            </Box>
          )}
          {apiData && apiData.data.length > 0 ? (
            apiData.data.map((position) => (
              <BillingPosition
                key={position.id}
                position={position}
                onSelectionChange={setWorkPackageIds}
                selection={workPackageIds}
              />
            ))
          ) : (
            <Box className="flex justify-center mt-2">
              <NoDataAnimation />
            </Box>
          )}
        </Box>
      </Box>
      <Box className="flex justify-between p-4">
        <Button
          color="error"
          variant="outlined"
          onClick={onClose}
          sx={{ visibility: onClose ? 'visible' : 'hidden' }}
        >
          {t('Cancel')}
        </Button>

        <Button
          color="success"
          variant="contained"
          disabled={workPackageIds.length == 0 || !isValid}
          onClick={handleSubmit}
        >
          {t('Generate Positions')}
        </Button>
      </Box>
    </Box>
  );
};

export default SelectTimeBilling;
